import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";

export interface SessionData {
  id?: number;
  name: string;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  schoolId: number;
  createdAt?: number[];
  updatedAt?: number[];
}

const API_URL = `${process.env.REACT_APP_API_URL}/sessions`;

// Fetch sessions by schoolId
const getSessionsBySchool = async (schoolId: number): Promise<SessionData[]> => {
  const response = await axios.get(`${API_URL}?schoolId=${schoolId}`);
  return response.data;
};

// Get active session for a school
const getActiveSession = async (schoolId: number): Promise<SessionData> => {
  const response = await axios.get(`${API_URL}/active?schoolId=${schoolId}`);
  return response.data;
};

// Create a new session
const createSession = async (sessionData: SessionData): Promise<SessionData> => {
  try {
    const response = await axios.post(API_URL, sessionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Activate a session
const activateSession = async (id: number): Promise<SessionData> => {
  try {
    const response = await axios.patch(`${API_URL}/${id}/activate`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a session
const deleteSession = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

// Add this helper function at the top
const isCurrentSession = (sessionName: string): boolean => {
  const [startYear, endYear] = sessionName.split('-');
  const currentYear = new Date().getFullYear();
  return parseInt(startYear) === currentYear;
};

// Update the createSessionFromName function
const createSessionFromName = async (sessionName: string, schoolId: number): Promise<SessionData> => {
  try {
    const [startYear, endYear] = sessionName.split('-');
    const startDate = `${startYear}-04-01`; // Academic year typically starts in April
    const endDate = `${endYear}-03-31`;     // Academic year typically ends in March

    const sessionData: SessionData = {
      name: sessionName,
      startDate,
      endDate,
      isActive: isCurrentSession(sessionName), // Set isActive based on whether it's current session
      schoolId
    };

    const response = await axios.post(API_URL, sessionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update a session
const updateSession = async (id: number, sessionData: SessionData): Promise<SessionData> => {
  const response = await axios.put(`${API_URL}/${id}`, sessionData);
  return response.data;
};

export function useSessions(schoolId?: number) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const sessionsQuery = useQuery<SessionData[], Error>({
    queryKey: ["sessions", schoolId],
    queryFn: () => {
      if (!schoolId) return Promise.resolve([]);
      return getSessionsBySchool(schoolId);
    },
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const activeSessionQuery = useQuery<SessionData, Error>({
    queryKey: ["activeSession", schoolId],
    queryFn: () => {
      if (!schoolId) throw new Error(t("school_id_required"));
      return getActiveSession(schoolId);
    },
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const createSessionMutation = useMutation({
    mutationFn: createSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions", schoolId] });
      queryClient.invalidateQueries({ queryKey: ["activeSession", schoolId] });
    },
  });

  const activateSessionMutation = useMutation({
    mutationFn: activateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions", schoolId] });
      queryClient.invalidateQueries({ queryKey: ["activeSession", schoolId] });
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions", schoolId] });
      queryClient.invalidateQueries({ queryKey: ["activeSession", schoolId] });
    },
  });

  const createSessionFromNameMutation = useMutation({
    mutationFn: (sessionName: string) => {
      if (!schoolId) throw new Error(t("school_id_required"));
      return createSessionFromName(sessionName, schoolId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions", schoolId] });
      queryClient.invalidateQueries({ queryKey: ["activeSession", schoolId] });
    },
  });

  const updateSessionMutation = useMutation({
    mutationFn: (args: { id: number; data: SessionData }) => updateSession(args.id, args.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions", schoolId] });
      queryClient.invalidateQueries({ queryKey: ["activeSession", schoolId] });
    },
    }
  );

  return {
    sessionsQuery,
    activeSessionQuery,
    createSessionMutation,
    activateSessionMutation,
    deleteSessionMutation,
    createSessionFromNameMutation,
    updateSessionMutation,
  };
}